// apps/web/src/api/authEvents.ts
// Cross-tab auth event channel and single emission point for session-lost
// propagation. Payload is non-secret — `{ type, reason, at }` only.
//
// @aiPurpose when one tab observes authoritative auth-loss (refresh 4xx,
//   manual logout, password change), other tabs in the same browser
//   profile converge to logged-out state without firing their own
//   `/api/auth/refresh`.
// @aiInvariant the wire payload is locked: no access tokens, refresh
//   tokens, cookies, user id/email/role/permissions, request ids, or raw
//   backend error bodies. A strict parse rejects any extra keys.
// @aiSecurity receiving a session-lost event clears local auth state; the
//   broadcast does not transfer credentials and does not re-authenticate.
import { useAuthStore } from '../stores/auth.js';

const CHANNEL_NAME = 'magimp:auth-events:v1';
const STORAGE_KEY = 'magimp:auth-events:v1';

export type SessionLostReason =
  | 'AUTH_REFRESH_INVALID'
  | 'AUTH_REFRESH_REUSED'
  | 'AUTH_REFRESH_4XX'
  | 'LOGOUT'
  | 'PASSWORD_CHANGED'
  | 'SESSION_CLEARED';

const REASON_VALUES: ReadonlyArray<SessionLostReason> = [
  'AUTH_REFRESH_INVALID',
  'AUTH_REFRESH_REUSED',
  'AUTH_REFRESH_4XX',
  'LOGOUT',
  'PASSWORD_CHANGED',
  'SESSION_CLEARED',
];

export interface SessionLostEvent {
  type: 'session-lost';
  reason: SessionLostReason;
  at: number;
}

const ALLOWED_EVENT_KEYS = new Set(['type', 'reason', 'at']);

let bc: BroadcastChannel | null = null;
let storageListenerInstalled = false;
let storageHandler: ((e: StorageEvent) => void) | null = null;
// INVARIANT: when `clearWithBroadcast` is invoked from the receive path it
// must NOT rebroadcast. The receive path sets this flag, calls clear(), and
// restores it. Direct subscribers see every event regardless.
let suppressEmit = false;
const listeners = new Set<(ev: SessionLostEvent) => void>();

function isReason(v: unknown): v is SessionLostReason {
  return typeof v === 'string' && (REASON_VALUES as readonly string[]).includes(v);
}

function parseAuthEvent(raw: unknown): SessionLostEvent | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (o.type !== 'session-lost') return null;
  if (typeof o.at !== 'number' || !Number.isFinite(o.at)) return null;
  if (!isReason(o.reason)) return null;
  for (const k of Object.keys(o)) {
    if (!ALLOWED_EVENT_KEYS.has(k)) return null;
  }
  return { type: 'session-lost', reason: o.reason, at: o.at };
}

function getBroadcastChannel(): BroadcastChannel | null {
  if (bc) return bc;
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    bc = new BroadcastChannel(CHANNEL_NAME);
    bc.onmessage = (msg: MessageEvent<unknown>) => {
      const ev = parseAuthEvent(msg.data);
      if (ev) onReceive(ev);
    };
    return bc;
  } catch {
    bc = null;
    return null;
  }
}

function installStorageListener(): void {
  if (storageListenerInstalled) return;
  if (typeof window === 'undefined') return;
  storageHandler = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY || !e.newValue) return;
    try {
      const raw: unknown = JSON.parse(e.newValue);
      const ev = parseAuthEvent(raw);
      if (ev) onReceive(ev);
    } catch {
      // ignore malformed
    }
  };
  window.addEventListener('storage', storageHandler);
  storageListenerInstalled = true;
}

function onReceive(ev: SessionLostEvent): void {
  suppressEmit = true;
  try {
    useAuthStore.getState().clear();
  } finally {
    suppressEmit = false;
  }
  for (const l of listeners) l(ev);
}

/**
 * @aiPurpose single emission point for cross-tab session-lost. Calls
 *   `useAuthStore.clear()` locally then broadcasts a non-secret event so
 *   other same-origin tabs converge to logged-out state.
 * @aiSecurity payload is `{ type, reason, at }` only — no tokens, no user
 *   data, no request ids, no raw error bodies.
 */
export function clearWithBroadcast(reason: SessionLostReason): void {
  useAuthStore.getState().clear();
  if (suppressEmit) return;
  const ev: SessionLostEvent = { type: 'session-lost', reason, at: Date.now() };
  const channel = getBroadcastChannel();
  if (channel) {
    try {
      channel.postMessage(ev);
    } catch {
      // ignore — degraded silently
    }
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ev));
    // Remove immediately so subsequent identical events still fire `storage`
    // (newValue change requirement). Receivers parse the `newValue` payload.
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort
  }
}

/**
 * Register a listener for received session-lost events. Returns an
 * unsubscribe function. Listeners fire AFTER local clear has been applied.
 */
export function subscribeAuthEvents(listener: (ev: SessionLostEvent) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * @aiPurpose initialize the cross-tab auth event listener. Idempotent.
 *   Call once at app startup (see `apps/web/src/main.tsx`).
 *   Returns a teardown function for tests/HMR.
 */
export function startAuthEventsListener(): () => void {
  getBroadcastChannel();
  installStorageListener();
  return () => {
    if (bc) {
      try {
        bc.close();
      } catch {
        // ignore
      }
      bc = null;
    }
    if (storageHandler && typeof window !== 'undefined') {
      window.removeEventListener('storage', storageHandler);
    }
    storageHandler = null;
    storageListenerInstalled = false;
  };
}

/**
 * @internal Test-only.
 */
export function __resetAuthEventsForTesting(): void {
  if (import.meta.env.MODE !== 'test') {
    throw new Error('__resetAuthEventsForTesting is test-only');
  }
  if (bc) {
    try {
      bc.close();
    } catch {
      // ignore
    }
    bc = null;
  }
  if (storageHandler && typeof window !== 'undefined') {
    window.removeEventListener('storage', storageHandler);
  }
  storageHandler = null;
  storageListenerInstalled = false;
  suppressEmit = false;
  listeners.clear();
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export const __TESTING__ = {
  CHANNEL_NAME,
  STORAGE_KEY,
};
